import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { ConnectProto } from "@racoon/api";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useActiveChildOrgs } from "~/shared/api/organization/use-active-child-orgs";
import { useHeldWhileLoading } from "~/shared/hooks/use-held-while-loading";
import { useListDestinationLocationsQuery } from "./locations.grpc";

const MAP_LOCATION_TYPES = [
  ConnectProto.LocationType.DEPOT,
  ConnectProto.LocationType.DISPOSAL_SITE,
  ConnectProto.LocationType.GAS_STATION,
  ConnectProto.LocationType.PROCESSING_PLANT,
  ConnectProto.LocationType.SORTING_COMPLEX,
  ConnectProto.LocationType.TRANSFER_STATION,
];

/**
 * The fixed sites drawn on the dashboard map, shared with their info card so both hit one cache
 * entry. Destination locations add the disposal operators picked in the org's settings, where its
 * trucks unload; child orgs own their own bases and transfer stations, which the RPC doesn't expand.
 */
export function useMapLocations() {
  const { activeCompanyId } = useAuthSession();
  const { children, isLoading: childrenLoading } = useActiveChildOrgs();
  const ownerOrganizationIds = useMemo(
    () => (activeCompanyId ? [BigInt(activeCompanyId), ...children.map((o) => BigInt(o.id))] : []),
    [activeCompanyId, children],
  );
  const query = useListDestinationLocationsQuery(
    activeCompanyId && !childrenLoading
      ? { ownerOrganizationIds, types: MAP_LOCATION_TYPES }
      : skipToken,
  );
  // Held per company; loading spans the child-org lookup, while the query is skipped.
  const data = useHeldWhileLoading(query.currentData, String(activeCompanyId), query.isError);
  return { ...query, data, isLoading: childrenLoading || (!data && query.isFetching) };
}
